// src/pages/personal/analytics/DashboardSection/components/SalesChannels/ChannelsChart.tsx
import React from 'react';
import { PieChart } from '@/components/ui/Charts';
import { useTranslation } from 'react-i18next';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { useResponsive } from '@/hooks/useResponsive';

interface ChartData {
  name: string;
  value: number;
  percentage: number;
  color: string;
  total: number;
  formattedPercentage?: string;
}

interface ChannelsChartProps {
  data: ChartData[];
}

export const ChannelsChart: React.FC<ChannelsChartProps> = ({ data }) => {
  const { t } = useTranslation('dashboard');
  const { formatPrice } = useBusinessCurrency();
  const { isMobile, isTablet } = useResponsive();
  
  // Вычисляем оптимальные размеры для круговой диаграммы
  // в зависимости от устройства
  const calcPieChartDimensions = () => {
    if (isMobile) {
      return {
        innerRadius: 35,
        outerRadius: 70,
        labelThreshold: 10, // Показывать метки только для сегментов >= 10% на мобильных
      };
    } else if (isTablet) {
      return {
        innerRadius: 40,
        outerRadius: 80,
        labelThreshold: 8, // Показывать метки только для сегментов >= 8% на планшетах
      };
    } else {
      return {
        innerRadius: 45,
        outerRadius: 90,
        labelThreshold: 5, // Показывать метки только для сегментов >= 5% на десктопах
      };
    }
  };
  
  const { innerRadius, outerRadius, labelThreshold } = calcPieChartDimensions();
  
  // Преобразуем данные в формат, требуемый компонентом PieChart
  const pieData = data.map(item => ({
    name: item.name,
    value: item.value,
    color: item.color,
    total: item.total,
    percentage: item.percentage,
    formattedPercentage: item.formattedPercentage
  }));

  // Расширенный тултип с дополнительной информацией
  const renderCustomTooltip = (props: any) => {
    const { active, payload } = props;
    
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="custom-tooltip bg-white p-2 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium" style={{ color: data.color }}>{data.name}</p>
          <p className="text-gray-700">{t('dashboard:sales.channels.orders')}: {data.value}</p>
          <p className="text-gray-700">{data.formattedPercentage}</p>
          <p className="text-gray-700">{t('dashboard:sales.channels.revenue')}: {formatPrice(data.total)}</p>
        </div>
      );
    }
    
    return null;
  };

  return (
    <PieChart
      data={pieData}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      showLegend={!isMobile} // Скрываем легенду на мобильных устройствах
      showLabels={!isMobile} // Скрываем метки на мобильных устройствах для экономии места
      paddingAngle={1} // Небольшой отступ между сегментами для лучшей визуализации
      tooltip={renderCustomTooltip}
      labelThreshold={labelThreshold} // Адаптивный порог для отображения меток
    />
  );
};

export default ChannelsChart;